import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, ArrowRight, CheckCircle2, XCircle, Loader2, Send, RotateCcw, ExternalLink, ChevronDown } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageHeader, Pill, TrustBadge, trustFor, Empty } from "./ui";

const EXAMPLES = [
  "Pharma companies in Hyderabad",
  "EV manufacturers in India",
  "Solar EPC companies in Gujarat",
  "Automobile exporters in Chennai",
  "Food processing companies in UAE",
  "Technology companies looking for strategic partnerships",
];
const INDUSTRIES = ["Manufacturing", "Healthcare", "Pharma", "Infrastructure", "Construction", "Renewable Energy", "EV & Mobility", "Food Processing", "Logistics", "Hospitality", "Technology", "Real Estate", "Agriculture", "Chemicals", "Education", "Financial Services", "Cross-Industry"];

function ResultCard({ p, onInvite }) {
  const [score, setScore] = useState(null);
  const [open, setOpen] = useState(false);
  const loadScore = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!score) {
      try { const { data } = await api.get(`/admin/prospects/${p.token}/score-detail`); setScore(data); } catch { /* keep card */ }
    }
  };
  return (
    <div className="adm-card p-5 hover:border-[#2563EB]/40 transition-colors" data-testid={`result-card-${p.token}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[15px] font-bold text-[#0B1B33]">{p.company_name}</h3>
            <TrustBadge type={trustFor(p)} />
          </div>
          <p className="mt-1 text-[12px] text-[#5B6B87]">{p.industry} · {[p.city, p.country].filter(Boolean).join(", ")}</p>
        </div>
        <div className="text-right">
          <p className="text-[9px] font-mono uppercase tracking-widest text-[#7C8AA3]">10X Opportunity</p>
          <p className="text-xl font-bold font-mono bg-gradient-to-r from-[#2563EB] to-[#7C3AED] bg-clip-text text-transparent" data-testid={`score-${p.token}`}>{p.ai_score ?? "—"}<span className="text-[11px] text-[#93A3BC]"> /100</span></p>
        </div>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-2 text-[12px]">
        <div className="flex items-center gap-2 text-[#22355A]">
          {p.website_verified ? <CheckCircle2 size={13} className="text-emerald-600" /> : <XCircle size={13} className="text-amber-500" />}
          {p.website_verified ? "Website Verified" : "Verification pending"}
          {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="text-[#2563EB] inline-flex items-center gap-0.5" data-testid={`visit-site-${p.token}`}><ExternalLink size={11} /></a>}
        </div>
        <div className="text-[#22355A] truncate">
          {p.contact_email
            ? <span className="flex items-center gap-2"><CheckCircle2 size={13} className="text-emerald-600" />{p.contact_email} <span className="text-[10px] font-mono text-emerald-600">VALID</span></span>
            : <span className="text-[#93A3BC]">Public email not found</span>}
        </div>
      </div>
      <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#93A3BC]">Source: {p.source || "discovery"} {p.created_at && `· ${String(p.created_at).slice(0, 10)}`}</p>
      {open && score && (
        <div className="mt-3 border border-[#E4EAF3] rounded-lg p-4 bg-[#F8FAFE]" data-testid={`score-breakdown-${p.token}`}>
          <div className="flex items-center justify-between mb-2.5">
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#7C8AA3]">Score Breakdown</p>
            <TrustBadge type={score.basis?.includes("AI") ? "AI-GENERATED" : "DERIVED"} />
          </div>
          <div className="space-y-1.5">
            {Object.entries(score.breakdown).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="text-[11px] text-[#43546D] w-36 shrink-0">{k}</span>
                <div className="flex-1 h-1.5 bg-[#E4EAF3] rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED]" style={{ width: `${v}%` }} />
                </div>
                <span className="text-[11px] font-mono font-bold text-[#0B1B33] w-6 text-right">{v}</span>
              </div>
            ))}
          </div>
          {score.ai_reason && <p className="mt-2.5 text-[11px] text-[#5B6B87] italic">AI insight: {score.ai_reason}</p>}
        </div>
      )}
      <div className="mt-4 flex gap-2">
        <button onClick={loadScore} data-testid={`view-company-${p.token}`} className="adm-btn-ghost !px-4 !py-2.5 flex-1">
          View Company <ChevronDown size={12} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
        </button>
        <button onClick={() => onInvite(p)} disabled={!p.contact_email || !p.website_verified || p.status === "INVITED" || p.status === "MEMBER"}
          data-testid={`invite-${p.token}`} className="adm-btn !px-4 !py-2.5 flex-1">
          {p.status === "INVITED" ? "Invited ✓" : p.status === "MEMBER" ? "Member" : "Invite"} <Send size={12} />
        </button>
      </div>
    </div>
  );
}

function InvitePanel({ p, campaign, onClose }) {
  const [mode, setMode] = useState("MANUAL");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/prospects/${p.token}/invite`);
      setResult({ ok: true, ...data });
      toast.success("Invitation sent");
    } catch (err) {
      setResult({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail), code: err.response?.status });
    } finally { setSending(false); }
  };
  const checks = [
    ["Website", p.website_verified ? "✓ Verified" : "Not verified", p.website_verified],
    ["Business Email", p.contact_email || "None", !!p.contact_email],
    ["Email Status", p.contact_email ? "Valid (format + domain)" : "—", !!p.contact_email],
    ["Duplicate", "Clear — checked again at send", true],
    ["Suppression", "Clear — checked again at send", true],
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="invite-panel">
      <div className="absolute inset-0 bg-[#0B1B33]/50" onClick={onClose} />
      <div className="relative adm-card w-full max-w-md p-6 shadow-2xl">
        <p className="adm-kicker mb-1">One-Click Invitation</p>
        <h3 className="text-lg font-bold text-[#0B1B33]" data-testid="invite-company">{p.company_name}</h3>
        <p className="text-[11px] text-[#7C8AA3] font-mono mt-0.5 mb-4">Campaign: {campaign}</p>
        <div className="space-y-2 mb-5">
          {checks.map(([label, val, ok]) => (
            <div key={label} className="flex items-center justify-between text-[12px] border-b border-[#EEF2F8] pb-2">
              <span className="text-[#5B6B87] font-mono uppercase text-[10px] tracking-widest">{label}</span>
              <span className={`flex items-center gap-1.5 font-medium ${ok ? "text-emerald-700" : "text-amber-600"}`}>
                {ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />} {val}
              </span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mb-5" data-testid="invite-mode">
          {[["MANUAL", "Manual — you review every send"], ["CONTROLLED_AUTO", "Controlled automation — eligibility engine gates"]].map(([v, label]) => (
            <button key={v} onClick={() => setMode(v)} data-testid={`mode-${v.toLowerCase()}`}
              className={`flex-1 text-left border rounded-lg px-3 py-2.5 text-[11px] transition-colors ${mode === v ? "border-[#2563EB] bg-[#2563EB]/5 text-[#0B1B33]" : "border-[#E4EAF3] text-[#7C8AA3]"}`}>
              <span className="font-bold block">{v === "MANUAL" ? "MANUAL" : "CONTROLLED AUTO"}</span>{label}
            </button>
          ))}
        </div>
        {result && (
          <div className={`mb-4 border rounded-lg p-3.5 text-[12px] ${result.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-700"}`} data-testid="invite-result">
            {result.ok ? (
              <>
                <p className="font-bold flex items-center gap-1.5"><CheckCircle2 size={14} /> Invitation sent ✓</p>
                <p className="font-mono text-[10.5px] mt-1 break-all">Provider message ID: {result.email_id}</p>
                <p className="font-mono text-[10.5px]">Timestamp: {new Date().toISOString()}</p>
              </>
            ) : (
              <>
                <p className="font-bold flex items-center gap-1.5"><XCircle size={14} /> Send blocked (HTTP {result.code})</p>
                <p className="mt-1">{result.error}</p>
                <p className="text-[10.5px] mt-1 font-mono">Action: fix the cause, then retry — nothing was marked sent.</p>
              </>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={onClose} data-testid="invite-cancel" className="adm-btn-ghost flex-1">Close</button>
          {!result?.ok && (
            <button onClick={send} disabled={sending} data-testid="send-invitation-btn" className="adm-btn flex-1">
              {sending ? <Loader2 size={13} className="animate-spin" /> : "Send Invitation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState(null);
  const [interpretation, setInterpretation] = useState("");
  const [planning, setPlanning] = useState(false);
  const [run, setRun] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [history, setHistory] = useState([]);
  const [inviteTarget, setInviteTarget] = useState(null);
  const pollRef = useRef(null);

  const loadHistory = () => api.get("/admin/search/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); return () => clearInterval(pollRef.current); }, []);

  const understand = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3) { toast.error("Type a business search first"); return; }
    setPlanning(true); setPlan(null); setRun(null); setProspects([]);
    try {
      const { data } = await api.post("/admin/search/plan", { query: q });
      setPlan(data.plan); setInterpretation(data.interpretation);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setPlanning(false); }
  };

  const runDiscovery = async () => {
    setRun({ status: "RUNNING", stages_done: ["Request understood"], counts: {} });
    try {
      const { data } = await api.post("/admin/growth/run", { query: q });
      const runId = data.run_id;
      pollRef.current = setInterval(async () => {
        try {
          const { data: r } = await api.get(`/admin/runs/${runId}`);
          setRun(r);
          if (r.status !== "RUNNING") {
            clearInterval(pollRef.current);
            const { data: all } = await api.get("/admin/prospects");
            const mine = all.filter((p) => p.run_id === runId);
            setProspects(mine);
            api.post("/admin/search/history", { query: q, plan, results_count: mine.length }).then(loadHistory).catch(() => {});
          }
        } catch { clearInterval(pollRef.current); }
      }, 2500);
    } catch (err) {
      setRun(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="global-search-page">
      <PageHeader kicker="Global Search" title="WHAT ARE YOU LOOKING FOR?"
        sub="Search in plain language. 10XGLOBAL converts it into a verified discovery plan — you review before anything runs." />

      <form onSubmit={understand} className="adm-card p-4 md:p-5 flex flex-col sm:flex-row gap-3" data-testid="global-search-form">
        <div className="flex-1 flex items-center gap-3 border border-[#D6DFEC] rounded-lg px-4 focus-within:border-[#2563EB] focus-within:shadow-[0_0_0_3px_rgba(37,99,235,.12)] transition-all">
          <Search size={17} className="text-[#93A3BC] shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pharma companies in Hyderabad"
            data-testid="global-search-input" className="flex-1 py-3.5 text-[14px] bg-transparent focus:outline-none placeholder:text-[#93A3BC]" />
        </div>
        <button type="submit" disabled={planning} data-testid="global-search-btn" className="adm-btn !px-8">
          {planning ? <Loader2 size={13} className="animate-spin" /> : "Search"}
        </button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2" data-testid="search-examples">
        {EXAMPLES.map((ex) => (
          <button key={ex} onClick={() => setQ(ex)} data-testid={`example-${ex.slice(0, 20).toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="text-[11.5px] text-[#43546D] border border-[#E4EAF3] hover:border-[#2563EB] hover:text-[#2563EB] px-3 py-1.5 rounded-full transition-colors bg-white">{ex}</button>
        ))}
      </div>

      {plan && (
        <div className="adm-card mt-5 p-5 md:p-6" data-testid="query-understood-panel">
          <div className="flex flex-wrap items-center gap-2.5 mb-4">
            <span className="adm-pill text-emerald-700 border-emerald-300 bg-emerald-50" data-testid="query-understood-badge">✓ Query Understood</span>
            <TrustBadge type={interpretation === "AI-GENERATED" ? "AI-GENERATED" : "DERIVED"} />
            <span className="text-[10.5px] text-[#93A3BC]">Review and edit before searching — nothing runs without you.</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="adm-kicker !text-[#7C8AA3] block mb-1.5">Industry</label>
              <select value={plan.industry} onChange={(e) => setPlan({ ...plan, industry: e.target.value })} data-testid="plan-industry" className="adm-input">
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            {[["location", "Location", "plan-location"], ["intent", "Intent", "plan-intent"], ["company_type", "Company Type", "plan-company-type"]].map(([k, label, tid]) => (
              <div key={k}>
                <label className="adm-kicker !text-[#7C8AA3] block mb-1.5">{label}</label>
                <input value={plan[k]} onChange={(e) => setPlan({ ...plan, [k]: e.target.value })} data-testid={tid} className="adm-input" />
              </div>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={runDiscovery} data-testid="run-discovery-btn" className="adm-btn">Run Discovery <ArrowRight size={13} /></button>
            <button onClick={() => setPlan(null)} data-testid="edit-cancel-btn" className="adm-btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {run && (
        <div className="adm-card mt-5 p-5" data-testid="run-progress">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-[13px] font-semibold text-[#0B1B33] flex items-center gap-2">
              {run.status === "RUNNING" && <Loader2 size={14} className="animate-spin text-[#2563EB]" />}
              Discovery run — {run.name || q}
            </p>
            <Pill value={run.status} />
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(run.stages_done || []).map((s, i) => (
              <span key={i} className="text-[10px] font-mono text-[#2563EB] bg-[#2563EB]/5 border border-[#2563EB]/20 px-2 py-1 rounded-full">✓ {s}</span>
            ))}
          </div>
          {run.counts && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
              {Object.entries(run.counts).map(([k, v]) => (
                <div key={k} className="border border-[#EEF2F8] rounded-lg py-2">
                  <p className="font-mono font-bold text-[15px] text-[#0B1B33]">{v}</p>
                  <p className="text-[9px] font-mono uppercase tracking-widest text-[#93A3BC]">{k}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {prospects.length > 0 && (
        <div className="mt-6" data-testid="search-results">
          <p className="adm-kicker mb-3">{prospects.length} companies from this search</p>
          <div className="grid md:grid-cols-2 gap-4">
            {prospects.map((p) => <ResultCard key={p.token} p={p} onInvite={setInviteTarget} />)}
          </div>
        </div>
      )}
      {run && run.status !== "RUNNING" && prospects.length === 0 && (
        <div className="mt-6"><Empty text="The run finished without new companies — existing records may already cover this query (duplicates are merged)." /></div>
      )}

      <div className="mt-10" data-testid="search-history">
        <p className="adm-kicker mb-3">Search History</p>
        {history.length === 0 ? <Empty text="No searches yet — your queries, filters and results will be stored here." /> : (
          <div className="adm-card divide-y divide-[#EEF2F8]">
            {history.map((h, i) => (
              <div key={i} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`history-row-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-[#0B1B33] truncate">{h.query}</p>
                  <p className="text-[10px] font-mono text-[#93A3BC]">{String(h.ts).slice(0, 16).replace("T", " ")} · {h.results_count} results · {h.admin}</p>
                </div>
                <button onClick={() => { setQ(h.query); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`rerun-${i}`}
                  className="adm-btn-ghost !px-3.5 !py-2"><RotateCcw size={11} /> Re-run</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {inviteTarget && <InvitePanel p={inviteTarget} campaign={inviteTarget.run_id ? "Discovery run campaign" : "Manual review"} onClose={() => setInviteTarget(null)} />}
      <p className="mt-8 text-[10.5px] text-[#93A3BC] max-w-2xl">
        Trust policy: only lawful public business contacts from verified company websites are used. Every send passes the eligibility engine — verification, validation, suppression, duplicate and rate checks — regardless of mode.
        Full pipeline operations live in the <Link to="/admin/outreach" className="text-[#2563EB] font-medium">Outreach Console</Link>.
      </p>
    </div>
  );
}
